import React, { ReactNode, useContext, useEffect, useState } from 'react';

import MaintenanceWindowEditDialog from '@totem/components/maintenanceWindows/maintenanceWindowEditDialog';
import MaintenanceWindowDetailV2Context from '@totem/components/maintenanceWindowsV2/detail/MaintenanceWindowDetailV2Context';
import { MaintenanceWindow } from '@totem/components/maintenanceWindowsV2/types';
import useApprovalAction from '@totem/components/maintenanceWindowsV2/useApprovalAction';
import { getTicketIds } from '@totem/components/maintenanceWindowsV2/utilities';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { DEVICES_ENDPOINT, TICKET_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  id: string;
  children?: ReactNode;
};

const MaintenanceWindowDetailV2Container = ({ id, children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [data, setData] = useState<MaintenanceWindow>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [now, setNow] = useState<number>(Date.now());
  const [refreshCount, setRefreshCount] = useState<number>(0);
  const [editing, setEditing] = useState<boolean>(false);
  const [ticketNumbers, setTicketNumbers] = useState<Record<string, string>>(
    {},
  );

  const refresh = () => setRefreshCount((count) => count + 1);
  const { sending, sendApproval } = useApprovalAction(refresh);

  useEffect(() => {
    setLoading(true);

    setNotFound(false);

    fetch(`${DEVICES_ENDPOINT}/maintenanceWindows2/${id}`, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((result: MaintenanceWindow) => {
        setData(result);

        setNow(Date.now());
      })
      .catch(() => {
        setData(null);

        setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id, userProfile.organization.id, refreshCount, getApiToken]);

  // Linked tickets (usually one change request); a ticket the user can't read keeps the plain "Ticket" label.
  const ticketKey = data !== null ? getTicketIds(data).join(',') : '';
  useEffect(() => {
    if (ticketKey === '') {
      setTicketNumbers({});
      return;
    }
    Promise.all(
      ticketKey.split(',').map((ticketId) =>
        fetch(`${TICKET_ENDPOINT}/${ticketId}`, {
          method: 'GET',
          headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
        })
          .then((res) => (res.ok ? res.json() : null))
          .then((result: TicketQueryResult) =>
            result?.ticket?.number ? [ticketId, result.ticket.number] : null,
          )
          .catch(() => null),
      ),
    ).then((entries) =>
      setTicketNumbers(Object.fromEntries(entries.filter(Boolean))),
    );
  }, [ticketKey, getApiToken]);

  const handleAction = (action: string) => {
    switch (action) {
      case 'refresh':
        refresh();
        break;
      case 'edit':
        setEditing(true);
        break;
      case 'approve':
        sendApproval(id, 'approve');
        break;
      case 'reject':
        sendApproval(id, 'reject');
        break;
      default:
        break;
    }
  };

  return (
    <MaintenanceWindowDetailV2Context.Provider
      value={{
        loading,
        notFound,
        data,
        now,
        sending,
        ticketNumbers,
        onAction: handleAction,
      }}
    >
      {children}
      {editing && data !== null && (
        <MaintenanceWindowEditDialog
          open
          onClose={() => {
            setEditing(false);

            refresh();
          }}
          maintenanceWindow={data}
        />
      )}
    </MaintenanceWindowDetailV2Context.Provider>
  );
};

export default MaintenanceWindowDetailV2Container;
